import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { ReactComponent as LapturaMark } from "../../assets/brand/laptura-mark.svg";
import { AuthFooter, AuthShell } from "./AuthShell";
import { saveLoginMarkRect } from "../WelcomeOverlay";

export default function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, startWelcome, dismissWelcome } = useAuth();
  const navigate = useNavigate();
  const markRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    saveLoginMarkRect(markRef.current);
    setLoading(true);
    startWelcome(formData.username);

    const success = await login(formData.username, formData.password);
    if (success) {
      navigate("/Laptura/dashboard");
    } else {
      dismissWelcome();
    }

    setLoading(false);
  };

  return (
    <AuthShell>
          <div className="w-full min-w-0 max-w-md space-y-8 lg:rounded-2xl lg:border lg:border-lap-hairline lg:bg-lap-card/45 lg:p-10 lg:shadow-2xl lg:shadow-black/25">
            <div>
              <div className="flex justify-center">
                <div ref={markRef} data-testid="login-mark" className="inline-flex">
                  <LapturaMark className="h-12 w-auto text-lap-text sm:h-16" role="img" aria-label="Laptura" />
                </div>
              </div>
              <p className="mt-6 text-center text-xs font-semibold uppercase tracking-[0.24em] text-lap-accent-bright lg:mt-7">Driver access</p>
              <h2 className="mt-2 text-center text-2xl font-extrabold text-white sm:text-3xl">
                Sign in to Laptura
              </h2>
              <p className="mt-2 text-center text-sm text-lap-muted">
                New here?{" "}
                <Link to="/Laptura/register" className="font-medium text-lap-accent-bright hover:text-white">
                  Create an account
                </Link>
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-lap-text mb-2">
                Username or Email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="relative block w-full appearance-none rounded-lg border border-lap-hairline bg-lap-bg/70 px-3.5 py-3 text-white placeholder-lap-dim transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-lap-accent sm:text-sm"
                placeholder="Enter your username or email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={formData.username}
                onChange={handleChange}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-lap-text mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                className="relative block w-full appearance-none rounded-lg border border-lap-hairline bg-lap-bg/70 px-3.5 py-3 pr-11 text-white placeholder-lap-dim transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-lap-accent sm:text-sm"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 top-7 flex items-center pr-3.5"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-lap-muted hover:text-lap-text" />
                ) : (
                  <Eye className="h-5 w-5 text-lap-muted hover:text-lap-text" />
                )}
              </button>
            </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group relative flex w-full justify-center rounded-lg border border-transparent bg-lap-accent px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-lap-pressed focus:outline-none focus:ring-2 focus:ring-lap-accent focus:ring-offset-2 focus:ring-offset-lap-bg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <AuthFooter />
          </div>
    </AuthShell>
  );
}
